// The form that files a feature request: six fields, in a dialog stacked over the board
// (docs/superpowers/specs/2026-10-07-feature-requests-design.md, "What a request carries").
//
// The board renders it — <RequestForm kept onClose onFiled onOutcomeUnknown /> — from its
// "New request" button. request-form.js decides everything that can be decided without a
// DOM: the fields and their copy, what is refused before sending and why, the exact body,
// how a server refusal lands, and what a close does. This file renders and sequences.
//
// CLOSING ASKS FIRST when the form holds anything (request-form.js `hasText`). Modal sends
// every way out — Escape, the backdrop, its close button — through the `onClose` given to
// it, and so does Cancel here, so what a close does is decided in one place,
// `requestClose`, by request-form.js `closeAction`. As a dialog stacked over the board,
// Escape and the backdrop close only this one, and focus goes back to the "New request"
// button that opened it (Modal.jsx, modal-stack.js).
//
// EXCEPT AFTER AN UNKNOWN OUTCOME, when closing KEEPS the text and asks nothing. The person
// was just told to check the board before filing again, and the board is behind this form,
// so they have to close it to look; asking "discard?" there would make them choose between
// looking and keeping what they typed. The close hands the values and the message to the
// board (`onClose(kept)`), which passes them back as `kept` the next time "New request"
// opens the form, until a filing lands or "Discard" (shown only then) throws them away.
//
// WHILE THE REQUEST IS BEING SENT, IT CANNOT BE CLOSED. Closing would not stop the POST, so
// the request could land after the person was told nothing was filed, or fail with nobody
// left to tell. A refused close is announced in the status region rather than ignored, and
// the wait is bounded: the POST is aborted after FILE_TIMEOUT_MS (30 seconds), because
// apiFetch has no timeout and the page behind is inert, so a hung request would otherwise
// hold the whole app. Then the form says which way it went:
//
//   filed        the board closes the form and loads its list again;
//   refused      (a 4xx) it stays open, everything still typed, the refusal shown;
//   unknown      (no answer, the timeout, a 5xx, a 2xx whose answer is not a row) it stays
//                open saying the request MAY not have been filed and to check the board
//                first, and `onOutcomeUnknown` has the board load its list, so a request
//                that did land is there to be seen.
//
// If the form unmounts anyway mid-filing (the page navigating away under it), the POST is
// aborted and nothing is set or called once it settles.
//
// The status region (role="status") also says "Filing…" as soon as the press registers.
//
// The submit button is aria-disabled while sending, not disabled — disabling the focused
// button would drop focus to <body>, outside the dialog's key handler — and `submit`
// refuses the second press instead.
//
// THE FIRST FIELD IN TROUBLE GETS FOCUS, whether the form or the server found the trouble,
// so the person lands on the box to fix rather than having to read a list to find it. It
// is focused after the render that draws its complaint, so the complaint is already part
// of the field's description (aria-describedby) when a screen reader announces the field.

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { api } from '../../api/client.js';
import Modal from '../Modal.jsx';
import {
  AREA_OPTIONS,
  FIELDS,
  FILE_TIMEOUT_MS,
  IMPACT_OPTIONS,
  KEPT_NOTE,
  buildRequestBody,
  closeAction,
  createdFrom,
  emptyValues,
  fieldErrors,
  filingStatus,
  formLabel,
  submitFailure,
} from './request-form.js';

const OPTIONS = { area: AREA_OPTIONS, impact: IMPACT_OPTIONS };

const DISCARD_QUESTION = 'Discard this feature request? What you typed will be lost.';

// A busy control keeps focus and looks unavailable; the handler is what refuses the press.
const BUSY_LOOK = 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50';

/**
 * @param {object} props
 * @param {{values: object, message: string}|null} [props.kept]  what a close after an
 *   unknown outcome kept, to start from; null or absent for a blank form
 * @param {(kept?: {values: object, message: string}) => void} props.onClose  the form is
 *   to go, with nothing filed; `kept` is given when the text is to be kept, and absent
 *   when it was discarded or there was none
 * @param {(created: object) => void} props.onFiled  the server's row for the new request
 * @param {() => void} props.onOutcomeUnknown  the POST failed without a refusal, so it may
 *   have landed: the board should load its list again
 */
export default function RequestForm({ kept = null, onClose, onFiled, onOutcomeUnknown }) {
  const idBase = useId();
  const [values, setValues] = useState(() => kept?.values ?? emptyValues());
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(() => kept?.message ?? null);
  // The last filing may or may not have landed, and nobody knows which (request-form.js
  // `submitFailure`). A kept form was kept for exactly that reason.
  const [outcomeUnknown, setOutcomeUnknown] = useState(kept !== null);
  const [submitting, setSubmitting] = useState(false);
  // A close was tried while the filing is in flight; the status region says why nothing
  // closed. Cleared when the filing ends.
  const [closeRefused, setCloseRefused] = useState(false);
  // Read synchronously by `submit`, `requestClose` and `discard`; `submitting` above is the
  // same fact for rendering, and lags it by a render.
  const sending = useRef(false);
  // The in-flight POST's controller, so an unmount can abort it; `unmounted` stops a POST
  // that settles afterwards from setting state or calling the board.
  const filing = useRef(null);
  const unmounted = useRef(false);
  const inputs = useRef({});
  // Where focus lands when the form opens.
  const titleRef = useRef(null);
  // The field to focus once the complaints just set have been drawn, or null.
  const focusNext = useRef(null);

  useEffect(() => {
    // Set on every mount, not only the first: StrictMode mounts, unmounts and mounts again.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      filing.current?.abort(new DOMException('The form closed.', 'AbortError'));
    };
  }, []);

  useLayoutEffect(() => {
    const name = focusNext.current;
    if (name === null) return;
    focusNext.current = null;
    inputs.current[name]?.focus();
  }, [errors]);

  const id = (name, part = 'input') => `${idBase}-${name}-${part}`;

  function change(name, value) {
    setValues((was) => ({ ...was, [name]: value }));
    // An edited field's complaint is out of date; the others stand until the next submit.
    setErrors((was) => {
      if (!(name in was)) return was;
      const next = { ...was };
      delete next[name];
      return next;
    });
  }

  function requestClose() {
    switch (closeAction({ sending: sending.current, outcomeUnknown, values })) {
      case 'refuse':
        setCloseRefused(true);
        return;
      case 'keep':
        onClose({ values, message });
        return;
      case 'ask':
        if (window.confirm(DISCARD_QUESTION)) onClose();
        return;
      default:
        onClose();
    }
  }

  // The one way to throw away what a close after an unknown outcome would keep.
  function discard() {
    if (sending.current) return;
    if (window.confirm(DISCARD_QUESTION)) onClose();
  }

  async function submit(event) {
    event.preventDefault();
    if (sending.current) return;

    const { problems, body, focus } = buildRequestBody(values);
    if (body === null) {
      focusNext.current = focus;
      setErrors(fieldErrors(problems));
      setMessage(null);
      return;
    }

    sending.current = true;
    setSubmitting(true);
    setMessage(null);
    const controller = new AbortController();
    filing.current = controller;
    const timer = setTimeout(
      () => controller.abort(new DOMException('No answer in time.', 'TimeoutError')),
      FILE_TIMEOUT_MS,
    );
    let created = null;
    let failed = null;
    try {
      created = createdFrom(await api.post('/feature-requests', body, { signal: controller.signal }));
    } catch (err) {
      // Once aborted, the reason is the abort's (the timeout, or the unmount), whatever the
      // fetch rejected with.
      failed = controller.signal.aborted ? controller.signal.reason : err;
    } finally {
      clearTimeout(timer);
      filing.current = null;
      sending.current = false;
    }
    if (unmounted.current) return;

    setSubmitting(false);
    setCloseRefused(false);
    if (failed === null) {
      onFiled(created);
      return;
    }
    const failure = submitFailure(failed);
    setErrors(failure.fieldErrors);
    setMessage(failure.message);
    setOutcomeUnknown(failure.outcomeUnknown);
    focusNext.current = FIELDS.find((field) => field.name in failure.fieldErrors)?.name ?? null;
    if (failure.outcomeUnknown) onOutcomeUnknown();
  }

  const status = filingStatus({ sending: submitting, closeRefused });

  function renderControl(field, describedBy) {
    const common = {
      id: id(field.name),
      ref: (element) => {
        inputs.current[field.name] = element;
        if (field.name === 'title') titleRef.current = element;
      },
      className: 'field-input',
      value: values[field.name],
      onChange: (event) => change(field.name, event.target.value),
      'aria-required': field.required || undefined,
      'aria-invalid': field.name in errors || undefined,
      'aria-describedby': describedBy || undefined,
    };
    if (field.kind === 'line') return <input type="text" autoComplete="off" {...common} />;
    if (field.kind === 'text') return <textarea rows={field.name === 'rules' ? 3 : 4} {...common} />;
    return (
      <select {...common}>
        <option value="">Choose…</option>
        {OPTIONS[field.name].map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  }

  return (
    <Modal
      title="New feature request"
      width="lg"
      onClose={requestClose}
      initialFocusRef={titleRef}
    >
      <form noValidate onSubmit={submit} className="space-y-4 p-4">
        {FIELDS.map((field) => {
          const tipId = field.tip ? id(field.name, 'tip') : null;
          const errorId = errors[field.name] ? id(field.name, 'error') : null;
          return (
            <div key={field.name}>
              <label className="field-label" htmlFor={id(field.name)}>
                {formLabel(field)}
                {field.required && (
                  <span aria-hidden="true" className="text-red-600 dark:text-red-400"> *</span>
                )}
              </label>
              {renderControl(field, [tipId, errorId].filter(Boolean).join(' '))}
              {field.tip && <p id={tipId} className="mt-1 text-xs text-ink-muted">{field.tip}</p>}
              {errorId && (
                <p id={errorId} className="mt-1 text-xs text-red-700 dark:text-red-300">{errors[field.name]}</p>
              )}
            </div>
          );
        })}

        {/* Always mounted, so a refusal is announced when it arrives. */}
        <div role="alert">
          {message && <p className="text-sm text-red-700 dark:text-red-300">{message}</p>}
          {/* On outcomeUnknown alone, not with the message: a later client-side refusal clears
              the message, and Close still keeps the text, so the note must stay to say so. */}
          {outcomeUnknown && <p className="mt-1 text-sm text-ink-secondary">{KEPT_NOTE}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Always mounted, so "Filing…" is heard when the press registers. While it is
              empty it is sr-only, which positions it out of the row, so it adds no gap. */}
          <span role="status" className={status ? 'text-sm text-ink-muted' : 'sr-only'}>
            {status}
          </span>
          {outcomeUnknown && (
            <button
              type="button"
              aria-disabled={submitting || undefined}
              className={`btn-ghost ring-1 ring-line-strong ${BUSY_LOOK}`}
              onClick={discard}
            >
              Discard
            </button>
          )}
          <button type="button" className="btn-ghost ring-1 ring-line-strong" onClick={requestClose}>
            {outcomeUnknown ? 'Close' : 'Cancel'}
          </button>
          <button
            type="submit"
            aria-disabled={submitting || undefined}
            className={`btn-primary ${BUSY_LOOK}`}
          >
            File request
          </button>
        </div>
      </form>
    </Modal>
  );
}
